import type { ReactElement } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { EllipsisIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { isIngestOwned } from '@/entities/brand-media';
import { usePermissions } from '@/entities/user';

/**
 * What can be done to one tag value, everywhere in the library.
 *
 * The counterpart to `FolderMenu` one rail over, and deliberately built the same way: the two sit
 * in one column, so a menu that opened differently or sat in a different place on the row would
 * read as a different kind of thing.
 *
 * Both entries here act on the whole workspace rather than on the grid's selection. Putting a tag
 * ON a selection is the separate control beside this one, which is the gesture people reach for
 * far more often and should not be buried behind a menu.
 */
export const TagValueMenu = ({
  tagKey,
  value,
  onRename,
  onDelete,
}: {
  tagKey: string;
  value: string;
  onRename: () => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation('tools');
  const { canManageBrandAssets } = usePermissions();

  const label = t('brandMedia.tagActions', { key: tagKey, value });
  // Renaming is open on every key. Deleting is not, for a key image analysis writes: the next
  // sweep of a row refills any such key it finds empty, so the deletion would come back on some
  // rows and not others. Mirrored from the server so the reason reads before the attempt.
  const deletable = !isIngestOwned(tagKey);

  /** A disabled item drops pointer events, so the span carries the hover that explains why. */
  const withReason = (item: ReactElement, allowed: boolean, reason: string) =>
    allowed ? (
      item
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block">{item}</span>
        </TooltipTrigger>
        <TooltipContent side="right">{reason}</TooltipContent>
      </Tooltip>
    );

  const noPermission = t('brandMediaLibrary.permissions.noPermission');

  return (
    <DropdownMenu>
      {/* The menu's trigger is the outer of the two, for the reason `FolderMenu` sets out: in an
          `asChild` chain the inner trigger's `data-state` wins, and reading the tooltip's state
          here would fade the button out while its own menu was still open. */}
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              // Matches the folder menu's own offset: without it a value row carrying this stands
              // taller than the key row above it, in the same list.
              className="-my-0.5 size-6 shrink-0 opacity-0 transition-opacity duration-fast ease-out group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
              aria-label={label}
              // The row underneath is a filter; opening its menu is not filtering by it.
              onClick={(event) => event.stopPropagation()}
            >
              <EllipsisIcon className="size-4" />
            </Button>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>

      <DropdownMenuContent
        align="start"
        // The row this sits in is itself a click target, so an item's click would otherwise bubble
        // out and toggle the filter on the tag being renamed or deleted.
        onClick={(event) => event.stopPropagation()}
      >
        {withReason(
          <DropdownMenuItem disabled={!canManageBrandAssets} onClick={onRename}>
            <PencilIcon className="size-4" />
            {t('brandMedia.rename')}
          </DropdownMenuItem>,
          canManageBrandAssets,
          noPermission,
        )}

        {withReason(
          <DropdownMenuItem
            variant="destructive"
            disabled={!canManageBrandAssets || !deletable}
            onClick={onDelete}
          >
            <Trash2Icon className="size-4" />
            {t('brandMedia.deleteTagEverywhere')}
          </DropdownMenuItem>,
          canManageBrandAssets && deletable,
          canManageBrandAssets ? t('brandMedia.modelTagKeeps', { key: tagKey }) : noPermission,
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
